export interface DeclineDataPoint {
  date: string;
  t: number;
  q: number;
}

export type DeclineModelName =
  | '指数递减'
  | '双曲递减'
  | '调和递减';

export interface DeclineFitResult {
  model: DeclineModelName;

  qi: number;

  /**
   * 初始名义递减率，单位 1/d
   */
  di: number;

  /**
   * 双曲指数。
   * 指数递减时为0；
   * 调和递减时为1。
   */
  b: number;

  r2: number;
  rmse: number;
  mae: number;
}

export interface DeclineForecastPoint {
  t: number;
  q: number;
}

function linearRegression(
  x: number[],
  y: number[],
) {
  if (
    x.length < 2 ||
    x.length !== y.length
  ) {
    return null;
  }

  const n = x.length;

  const meanX =
    x.reduce(
      (sum, value) =>
        sum + value,
      0,
    ) / n;

  const meanY =
    y.reduce(
      (sum, value) =>
        sum + value,
      0,
    ) / n;

  let numerator = 0;
  let denominator = 0;

  for (
    let i = 0;
    i < n;
    i += 1
  ) {
    numerator +=
      (x[i] - meanX) *
      (y[i] - meanY);

    denominator +=
      (x[i] - meanX) ** 2;
  }

  if (
    Math.abs(denominator) <
    1e-12
  ) {
    return null;
  }

  const slope =
    numerator / denominator;

  const intercept =
    meanY -
    slope * meanX;

  return {
    slope,
    intercept,
  };
}

function calculateMetrics(
  actual: number[],
  predicted: number[],
) {
  if (
    actual.length === 0 ||
    actual.length !==
      predicted.length
  ) {
    return {
      r2: NaN,
      rmse: NaN,
      mae: NaN,
    };
  }

  const mean =
    actual.reduce(
      (sum, value) =>
        sum + value,
      0,
    ) / actual.length;

  const ssTot =
    actual.reduce(
      (sum, value) =>
        sum +
        (value - mean) ** 2,
      0,
    );

  const ssRes =
    actual.reduce(
      (
        sum,
        value,
        index,
      ) =>
        sum +
        (
          value -
          predicted[index]
        ) **
          2,
      0,
    );

  const mse =
    ssRes /
    actual.length;

  const mae =
    actual.reduce(
      (
        sum,
        value,
        index,
      ) =>
        sum +
        Math.abs(
          value -
            predicted[index],
        ),
      0,
    ) /
    actual.length;

  return {
    r2:
      ssTot < 1e-12
        ? 1
        : 1 -
          ssRes / ssTot,

    rmse:
      Math.sqrt(mse),

    mae,
  };
}

export function predictDeclineRate(
  model: DeclineFitResult,
  t: number,
) {
  if (
    t < 0 ||
    model.qi <= 0 ||
    model.di < 0
  ) {
    return NaN;
  }

  if (
    model.model ===
    '指数递减'
  ) {
    return (
      model.qi *
      Math.exp(
        -model.di * t,
      )
    );
  }

  if (
    model.model ===
    '调和递减'
  ) {
    return (
      model.qi /
      (1 +
        model.di * t)
    );
  }

  return (
    model.qi /
    Math.pow(
      1 +
        model.b *
          model.di *
          t,
      1 / model.b,
    )
  );
}

export function fitExponentialDecline(
  data: DeclineDataPoint[],
): DeclineFitResult | null {
  const valid =
    data.filter(
      (point) =>
        point.t >= 0 &&
        point.q > 0 &&
        Number.isFinite(
          point.t,
        ) &&
        Number.isFinite(
          point.q,
        ),
    );

  if (valid.length < 3) {
    return null;
  }

  const x =
    valid.map(
      (point) =>
        point.t,
    );

  const y =
    valid.map(
      (point) =>
        Math.log(
          point.q,
        ),
    );

  const regression =
    linearRegression(x, y);

  if (!regression) {
    return null;
  }

  const qi =
    Math.exp(
      regression.intercept,
    );

  const di =
    -regression.slope;

  if (
    !Number.isFinite(qi) ||
    !Number.isFinite(di) ||
    qi <= 0 ||
    di < 0
  ) {
    return null;
  }

  const model:
    DeclineFitResult = {
      model:
        '指数递减',

      qi,
      di,
      b: 0,

      r2: 0,
      rmse: 0,
      mae: 0,
    };

  const actual =
    valid.map(
      (point) =>
        point.q,
    );

  const predicted =
    valid.map(
      (point) =>
        predictDeclineRate(
          model,
          point.t,
        ),
    );

  const metrics =
    calculateMetrics(
      actual,
      predicted,
    );

  return {
    ...model,
    ...metrics,
  };
}

export function fitHarmonicDecline(
  data: DeclineDataPoint[],
): DeclineFitResult | null {
  const valid =
    data.filter(
      (point) =>
        point.t >= 0 &&
        point.q > 0 &&
        Number.isFinite(
          point.t,
        ) &&
        Number.isFinite(
          point.q,
        ),
    );

  if (valid.length < 3) {
    return null;
  }

  const x =
    valid.map(
      (point) =>
        point.t,
    );

  const y =
    valid.map(
      (point) =>
        1 / point.q,
    );

  const regression =
    linearRegression(x, y);

  if (!regression) {
    return null;
  }

  const a =
    regression.intercept;

  const slope =
    regression.slope;

  if (
    a <= 0 ||
    slope < 0
  ) {
    return null;
  }

  const qi =
    1 / a;

  const di =
    slope / a;

  if (
    !Number.isFinite(qi) ||
    !Number.isFinite(di) ||
    qi <= 0 ||
    di < 0
  ) {
    return null;
  }

  const model:
    DeclineFitResult = {
      model:
        '调和递减',

      qi,
      di,
      b: 1,

      r2: 0,
      rmse: 0,
      mae: 0,
    };

  const actual =
    valid.map(
      (point) =>
        point.q,
    );

  const predicted =
    valid.map(
      (point) =>
        predictDeclineRate(
          model,
          point.t,
        ),
    );

  const metrics =
    calculateMetrics(
      actual,
      predicted,
    );

  return {
    ...model,
    ...metrics,
  };
}

export function fitHyperbolicDecline(
  data: DeclineDataPoint[],
): DeclineFitResult | null {
  const valid =
    data.filter(
      (point) =>
        point.t >= 0 &&
        point.q > 0 &&
        Number.isFinite(
          point.t,
        ) &&
        Number.isFinite(
          point.q,
        ),
    );

  if (valid.length < 3) {
    return null;
  }

  let best:
    DeclineFitResult | null =
    null;

  /*
   * 对b进行网格搜索。
   * 暂取0.10～2.00，
   * 后续可根据具体储层类型
   * 增加约束范围设置。
   */
  for (
    let step = 10;
    step <= 200;
    step += 1
  ) {
    const b =
      step / 100;

    const x =
      valid.map(
        (point) =>
          point.t,
      );

    /*
     * q^-b =
     * qi^-b *
     * (1 + bDi t)
     *
     * 因此：
     * q^-b = a + kt
     */
    const y =
      valid.map(
        (point) =>
          Math.pow(
            point.q,
            -b,
          ),
      );

    const regression =
      linearRegression(
        x,
        y,
      );

    if (!regression) {
      continue;
    }

    const a =
      regression.intercept;

    const slope =
      regression.slope;

    if (
      a <= 0 ||
      slope < 0
    ) {
      continue;
    }

    const qi =
      Math.pow(
        a,
        -1 / b,
      );

    const di =
      slope /
      (b * a);

    if (
      !Number.isFinite(qi) ||
      !Number.isFinite(di) ||
      qi <= 0 ||
      di < 0
    ) {
      continue;
    }

    const model:
      DeclineFitResult = {
        model:
          '双曲递减',

        qi,
        di,
        b,

        r2: 0,
        rmse: 0,
        mae: 0,
      };

    const actual =
      valid.map(
        (point) =>
          point.q,
      );

    const predicted =
      valid.map(
        (point) =>
          predictDeclineRate(
            model,
            point.t,
          ),
      );

    const metrics =
      calculateMetrics(
        actual,
        predicted,
      );

    const candidate = {
      ...model,
      ...metrics,
    };

    if (
      !best ||
      candidate.rmse <
        best.rmse
    ) {
      best =
        candidate;
    }
  }

  return best;
}

export function cumulativeDeclineProduction(
  model: DeclineFitResult,
  t: number,
) {
  if (
    t <= 0 ||
    model.qi <= 0
  ) {
    return 0;
  }

  if (
    model.di <
    1e-12
  ) {
    return (
      model.qi * t
    );
  }

  if (
    model.model ===
    '指数递减'
  ) {
    return (
      model.qi /
      model.di *
      (
        1 -
        Math.exp(
          -model.di *
            t,
        )
      )
    );
  }

  if (
    Math.abs(
      model.b - 1,
    ) < 1e-9
  ) {
    return (
      model.qi /
      model.di *
      Math.log(
        1 +
          model.di *
            t,
      )
    );
  }

  const base =
    1 +
    model.b *
      model.di *
      t;

  return (
    model.qi /
    (
      model.di *
      (1 - model.b)
    ) *
    (
      1 -
      Math.pow(
        base,
        (
          model.b - 1
        ) /
          model.b,
      )
    )
  );
}

export function timeToEconomicLimit(
  model: DeclineFitResult,
  economicLimit: number,
) {
  if (
    economicLimit <= 0 ||
    model.qi <= 0 ||
    economicLimit >=
      model.qi
  ) {
    return 0;
  }

  if (
    model.di <
    1e-12
  ) {
    return Infinity;
  }

  if (
    model.model ===
    '指数递减'
  ) {
    return (
      Math.log(
        model.qi /
          economicLimit,
      ) / model.di
    );
  }

  return (
    (
      Math.pow(
        model.qi /
          economicLimit,
        model.b,
      ) -
      1
    ) /
    (
      model.b *
      model.di
    )
  );
}

export function buildForecastCurve(
  model: DeclineFitResult,
  endT: number,
  stepDays = 10,
): DeclineForecastPoint[] {
  const curve:
    DeclineForecastPoint[] =
    [];

  const safeEnd =
    Math.max(
      0,
      endT,
    );

  for (
    let t = 0;
    t <= safeEnd;
    t += stepDays
  ) {
    curve.push({
      t,

      q:
        predictDeclineRate(
          model,
          t,
        ),
    });
  }

  if (
    curve.length === 0 ||
    curve[
      curve.length - 1
    ].t < safeEnd
  ) {
    curve.push({
      t: safeEnd,

      q:
        predictDeclineRate(
          model,
          safeEnd,
        ),
    });
  }

  return curve;
}

export function effectiveAnnualDecline(
  model: DeclineFitResult,
) {
  if (
    model.qi <= 0
  ) {
    return NaN;
  }

  const q365 =
    predictDeclineRate(
      model,
      365,
    );

  return (
    1 -
    q365 / model.qi
  );
}

/* =========================================================
 * 非常规储层递减模型
 * SEPD + Duong
 * ========================================================= */

export interface SepdFitResult {
  model: 'SEPD';
  qi: number;
  tau: number;
  n: number;
  r2: number;
  rmse: number;
  mae: number;
}

export interface DuongFitResult {
  model: 'Duong';
  q1: number;
  a: number;
  m: number;
  r2: number;
  rmse: number;
  mae: number;

  /**
   * log(q/Gp) - log(t) 诊断直线的 R²
   */
  diagnosticR2: number;
}

/* ---------- SEPD ---------- */

export function predictSepdRate(
  model: SepdFitResult,
  t: number,
) {
  if (
    t < 0 ||
    model.qi <= 0 ||
    model.tau <= 0 ||
    model.n <= 0
  ) {
    return NaN;
  }

  return (
    model.qi *
    Math.exp(
      -Math.pow(
        t / model.tau,
        model.n,
      ),
    )
  );
}

export function fitSepd(
  data: DeclineDataPoint[],
): SepdFitResult | null {
  const valid = data.filter(
    (point) =>
      point.t >= 0 &&
      point.q > 0 &&
      Number.isFinite(point.t) &&
      Number.isFinite(point.q),
  );

  if (valid.length < 4) {
    return null;
  }

  let best: SepdFitResult | null = null;

  /*
   * SEPD:
   * q = qi exp[-(t/tau)^n]
   *
   * 固定 n 后：
   * ln(q) = ln(qi) - t^n / tau^n
   *
   * 因此可对 ln(q) 与 t^n 线性回归。
   */
  for (
    let step = 10;
    step <= 150;
    step += 1
  ) {
    const n = step / 100;

    const x = valid.map(
      (point) =>
        Math.pow(point.t, n),
    );

    const y = valid.map(
      (point) =>
        Math.log(point.q),
    );

    const regression =
      linearRegression(x, y);

    if (!regression) {
      continue;
    }

    /*
     * slope 必须为负数
     */
    if (regression.slope >= 0) {
      continue;
    }

    const qi = Math.exp(
      regression.intercept,
    );

    const tau = Math.pow(
      -1 / regression.slope,
      1 / n,
    );

    if (
      !Number.isFinite(qi) ||
      !Number.isFinite(tau) ||
      qi <= 0 ||
      tau <= 0
    ) {
      continue;
    }

    const base: SepdFitResult = {
      model: 'SEPD',
      qi,
      tau,
      n,
      r2: 0,
      rmse: 0,
      mae: 0,
    };

    const actual = valid.map(
      (point) => point.q,
    );

    const predicted = valid.map(
      (point) =>
        predictSepdRate(
          base,
          point.t,
        ),
    );

    const metrics =
      calculateMetrics(
        actual,
        predicted,
      );

    const candidate: SepdFitResult = {
      ...base,
      ...metrics,
    };

    if (
      !best ||
      candidate.rmse <
        best.rmse
    ) {
      best = candidate;
    }
  }

  return best;
}

/* ---------- Duong ---------- */

function duongTimeFunction(
  t: number,
  a: number,
  m: number,
) {
  const safeT = Math.max(
    t,
    1e-6,
  );

  /*
   * m 接近 1 时单独处理，
   * 避免 1-m 分母趋近 0。
   */
  if (
    Math.abs(1 - m) <
    1e-6
  ) {
    return Math.pow(
      safeT,
      a - 1,
    );
  }

  return (
    Math.pow(safeT, -m) *
    Math.exp(
      (a / (1 - m)) *
        (
          Math.pow(
            safeT,
            1 - m,
          ) - 1
        ),
    )
  );
}

export function predictDuongRate(
  model: DuongFitResult,
  t: number,
) {
  /*
   * 原始生产数据第一天定义为 t = 1，
   * 所以内部采用 t + 1。
   */
  const td = Math.max(
    t + 1,
    1,
  );

  return (
    model.q1 *
    duongTimeFunction(
      td,
      model.a,
      model.m,
    )
  );
}

export function fitDuong(
  data: DeclineDataPoint[],
): DuongFitResult | null {
  const valid = [...data]
    .filter(
      (point) =>
        point.t >= 0 &&
        point.q > 0 &&
        Number.isFinite(point.t) &&
        Number.isFinite(point.q),
    )
    .sort(
      (a, b) =>
        a.t - b.t,
    );

  if (valid.length < 4) {
    return null;
  }

  /*
   * 由日产气量数值积分得到累计产气 Gp。
   * 单位：
   * q  = 10^4 m3/d
   * Gp = 10^4 m3
   */
  const regressionRows: {
    t: number;
    q: number;
    gp: number;
  }[] = [];

  let gp =
    valid[0].q;

  regressionRows.push({
    t: valid[0].t + 1,
    q: valid[0].q,
    gp,
  });

  for (
    let i = 1;
    i < valid.length;
    i += 1
  ) {
    const previous =
      valid[i - 1];

    const current =
      valid[i];

    const dt =
      current.t -
      previous.t;

    if (dt <= 0) {
      continue;
    }

    /*
     * 梯形积分
     */
    gp +=
      0.5 *
      (
        previous.q +
        current.q
      ) *
      dt;

    regressionRows.push({
      t: current.t + 1,
      q: current.q,
      gp,
    });
  }

  if (
    regressionRows.length <
    3
  ) {
    return null;
  }

  /*
   * Duong:
   *
   * q / Gp = a t^(-m)
   *
   * ln(q/Gp)
   * =
   * ln(a) - m ln(t)
   */
  const x =
    regressionRows.map(
      (row) =>
        Math.log(row.t),
    );

  const y =
    regressionRows.map(
      (row) =>
        Math.log(
          row.q / row.gp,
        ),
    );

  const regression =
    linearRegression(x, y);

  if (!regression) {
    return null;
  }

  const m =
    -regression.slope;

  const a =
    Math.exp(
      regression.intercept,
    );

  if (
    !Number.isFinite(a) ||
    !Number.isFinite(m) ||
    a <= 0 ||
    m <= 0
  ) {
    return null;
  }

  /*
   * 计算 q1。
   * q = q1 * f(t)
   *
   * 使用最小二乘求 q1，
   * 而不是简单取第一天日产量。
   */
  const fValues =
    valid.map(
      (point) =>
        duongTimeFunction(
          point.t + 1,
          a,
          m,
        ),
    );

  const numerator =
    valid.reduce(
      (
        sum,
        point,
        index,
      ) =>
        sum +
        point.q *
          fValues[index],
      0,
    );

  const denominator =
    fValues.reduce(
      (sum, value) =>
        sum + value * value,
      0,
    );

  if (
    denominator <=
    1e-12
  ) {
    return null;
  }

  const q1 =
    numerator /
    denominator;

  if (
    !Number.isFinite(q1) ||
    q1 <= 0
  ) {
    return null;
  }

  const base: DuongFitResult = {
    model: 'Duong',
    q1,
    a,
    m,
    r2: 0,
    rmse: 0,
    mae: 0,
    diagnosticR2: 0,
  };

  const actual =
    valid.map(
      (point) =>
        point.q,
    );

  const predicted =
    valid.map(
      (point) =>
        predictDuongRate(
          base,
          point.t,
        ),
    );

  const metrics =
    calculateMetrics(
      actual,
      predicted,
    );

  /*
   * q/Gp - t 双对数诊断线 R²
   */
  const transformedPrediction =
    x.map(
      (value) =>
        regression.intercept +
        regression.slope *
          value,
    );

  const diagnostic =
    calculateMetrics(
      y,
      transformedPrediction,
    );

  return {
    ...base,
    ...metrics,
    diagnosticR2:
      diagnostic.r2,
  };
}

/* ---------- 数值积分 ---------- */

export function integrateForecastRate(
  rateFunction: (
    t: number,
  ) => number,
  endT: number,
  stepDays = 1,
) {
  if (
    endT <= 0 ||
    stepDays <= 0
  ) {
    return 0;
  }

  let cumulative = 0;

  let t0 = 0;

  let q0 =
    rateFunction(t0);

  while (t0 < endT) {
    const t1 =
      Math.min(
        t0 + stepDays,
        endT,
      );

    const q1 =
      rateFunction(t1);

    if (
      Number.isFinite(q0) &&
      Number.isFinite(q1)
    ) {
      cumulative +=
        0.5 *
        (q0 + q1) *
        (t1 - t0);
    }

    t0 = t1;
    q0 = q1;
  }

  return cumulative;
}